// Global Instructions Rule Applied!

import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FormTable } from '../../../../../core/components/FormTable/index.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import {
  FieldTypeEnum,
  ContactFieldConfig,
  SelectFieldConfig,
  TextFieldConfig
} from '../../../../../core/components/InputManager/types.ts'
import { UserEntry, UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import {
  CompanyTeamEntry,
  CompanyTeamEntryFields
} from '../../../../../state_manager/data_models/company_team_entry.ts'
import { RelationshipTypesModel } from '../../../../../state_manager/data_models/relationship_types_model.ts'
import { RelationshipCategoriesModel } from '../../../../../state_manager/data_models/relationship_categories_model.ts'
import {
  RELATIONSHIP_CATEGORY_INTRODUCED_BY,
  RELATIONSHIP_TYPE_ADVISOR,
  RELATIONSHIP_TYPE_OTHER
} from '../../../../../state_manager/globals.ts'
import { getColumnConfig } from '../../../../../core/components/CompanyTeamTable/utils/fieldConfigs.tsx'
import {
  createCustomValidationRule,
  validateAndShowErrors
} from '../../../../../core/components/FormTable/validation.ts'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG } from '../../../../../core/components/FormTable/constants.ts'

interface OtherTableProps {
  introducedBy?: UserEntry[]
  sourcedBy?: UserEntry[]
  otherTeam?: CompanyTeamEntry[]
  recordId?: string | null
  className?: string
  onSave?: (savedEntry: any, allEntries: any[]) => Promise<void>
  onDelete?: (entry: any) => Promise<void>
  readOnly?: boolean
}

interface OtherRow {
  key: string
  contactId: string
  contactName: string
  email: string
  type: string
  category: string
  relationshipTypeId: string
  relationshipCategoryId: string
  relationshipId: string | null
  originalEntry: CompanyTeamEntry | UserEntry | null
}

export function OtherTable({
  introducedBy = [],
  sourcedBy = [],
  otherTeam = [],
  recordId,
  className = '',
  onSave,
  onDelete,
  readOnly = false
}: OtherTableProps) {
  const relationshipCategoriesRaw = useSelector((state: any) => state.globalConfigs?.relationship_categories || [])
  const relationshipCategories = useMemo(() => relationshipCategoriesRaw || [], [relationshipCategoriesRaw])
  const relationshipTypesRaw = useSelector((state: any) => state.globalConfigs?.relationship_types || [])
  const relationshipTypes = useMemo(() => relationshipTypesRaw || [], [relationshipTypesRaw])

  // State management for inline editing
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null)
  const [savingRowKey, setSavingRowKey] = useState<string | null>(null)
  const [deletingRowKey, setDeletingRowKey] = useState<string | null>(null)
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, Record<string, string>>>({})
  const [firstErrorFieldKey, setFirstErrorFieldKey] = useState<{ rowKey: string; fieldKey: string } | null>(null)
  const [originalData, setOriginalData] = useState<any>(null)

  // Helper function to get category label by ID
  const getCategoryLabelById = useCallback(
    (categoryId: string | null | undefined): string => {
      if (!categoryId) return ''
      const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
      return category?.category || category?.name || ''
    },
    [relationshipCategories]
  )

  // Helper function to get type label by ID
  const getTypeLabelById = useCallback(
    (typeId: string | null | undefined): string => {
      if (!typeId) return ''
      const type = relationshipTypes.find((t: any) => t.id === typeId)
      return type?.type || type?.name || ''
    },
    [relationshipTypes]
  )

  // Helper function to get relationship type ID from category ID
  const getTypeIdFromCategoryId = useCallback(
    (categoryId: string | null | undefined): string | null => {
      if (!categoryId) return null
      const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
      return category?.relationship_type_id || category?.relationshipTypeId || null
    },
    [relationshipCategories]
  )

  // Transform UserEntry[] data to internal editable format
  // Use useMemo to recalculate when relationshipCategories loads or props change
  const transformedEntriesFromProps = useMemo(() => {
    const allEntries: OtherRow[] = []

    // Use otherTeam if provided, otherwise fallback to introducedBy + sourcedBy for backward compatibility
    // Filter out sourcing categories - only process Advisor and Other types
    const contactsToProcess: CompanyTeamEntry[] =
      otherTeam.length > 0
        ? otherTeam.filter((contact: any) => {
            const categoryId = contact[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
            if (!categoryId) return false

            // If relationshipCategories haven't loaded yet, include entries with categoryId (fail-open)
            // This prevents filtering out valid entries when Redux data isn't ready
            if (relationshipCategories.length === 0) {
              return true
            }

            const typeId = getTypeIdFromCategoryId(categoryId)
            return typeId === RELATIONSHIP_TYPE_ADVISOR || typeId === RELATIONSHIP_TYPE_OTHER
          })
        : [...introducedBy].filter((contact: any) => {
            // Only include introducedBy for backward compatibility, exclude sourcedBy
            return true
          })

    contactsToProcess.forEach((contact, index) => {
      const contactId = contact[UserEntryFields.contacts__id] || ''
      const displayName =
        contact[UserEntryFields.prog__display_name] ||
        `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
        contact[UserEntryFields.contacts__email] ||
        'Unknown'
      const relationshipId = contact[UserEntryFields.contact_accounts__id] || null
      // Try to get relationship_category_id from CompanyTeamEntryFields, fallback to checking introducedBy array
      let relationshipCategoryId = contact[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
      if (!relationshipCategoryId && otherTeam.length === 0) {
        // Fallback for backward compatibility: use introducedBy category
        relationshipCategoryId = RELATIONSHIP_CATEGORY_INTRODUCED_BY
      }

      // Determine relationship type ID from category ID
      const relationshipTypeId = getTypeIdFromCategoryId(relationshipCategoryId) || ''
      const typeLabel = getTypeLabelById(relationshipTypeId) || ''
      const categoryLabel = getCategoryLabelById(relationshipCategoryId) || ''

      allEntries.push({
        key: relationshipId || `other-${contactId}-${index}`,
        contactId,
        contactName: displayName,
        email: contact[UserEntryFields.contacts__email] || '-',
        type: typeLabel,
        category: categoryLabel,
        relationshipTypeId,
        relationshipCategoryId: relationshipCategoryId || '',
        relationshipId,
        originalEntry: contact
      })
    })

    // Sort by contact name alphabetically
    allEntries.sort((a, b) => a.contactName.localeCompare(b.contactName))

    return allEntries
  }, [otherTeam, introducedBy, relationshipCategories, getTypeIdFromCategoryId, getTypeLabelById, getCategoryLabelById])

  // Initialize state from transformed entries, but allow local editing state to override
  const [editableEntries, setEditableEntries] = useState<OtherRow[]>(transformedEntriesFromProps)

  // Update editableEntries when transformed entries change (but not when editing)
  // This syncs state when props change or when relationshipCategories loads
  const prevTransformedEntriesRef = useRef(transformedEntriesFromProps)
  const isUpdatingFromParentRef = useRef(false)

  useEffect(() => {
    const transformedChanged =
      JSON.stringify(prevTransformedEntriesRef.current) !== JSON.stringify(transformedEntriesFromProps)

    if (transformedChanged && !isAddingNew && !editingRowKey) {
      isUpdatingFromParentRef.current = true
      setEditableEntries(transformedEntriesFromProps)
      prevTransformedEntriesRef.current = transformedEntriesFromProps
      setTimeout(() => {
        isUpdatingFromParentRef.current = false
      }, 0)
    } else {
      prevTransformedEntriesRef.current = transformedEntriesFromProps
    }
  }, [transformedEntriesFromProps, isAddingNew, editingRowKey])

  // Type options for Select field - only Advisor and Other relationship types
  const typeOptions = useMemo(() => {
    if (!relationshipTypes || relationshipTypes.length === 0) {
      // Fallback to hardcoded options if Redux state not available
      return [
        { value: RELATIONSHIP_TYPE_ADVISOR, label: 'Advisor' },
        { value: RELATIONSHIP_TYPE_OTHER, label: 'Other' }
      ]
    }

    // Filter to only include Advisor and Other relationship types
    const filteredTypes: RelationshipTypesModel[] = relationshipTypes.filter((type: any) => {
      const typeId = type.id
      return typeId === RELATIONSHIP_TYPE_ADVISOR || typeId === RELATIONSHIP_TYPE_OTHER
    })

    // Map to options format and sort alphabetically
    return filteredTypes
      .map((type: any) => ({
        value: type.id,
        label: type.type || type.name || ''
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [relationshipTypes])

  // Get category options filtered by relationship type
  const getCategoryOptionsByType = useCallback(
    (relationshipTypeId: string | null | undefined) => {
      if (!relationshipTypeId) return []

      const filteredCategories: RelationshipCategoriesModel[] = relationshipCategories.filter((cat: any) => {
        const catTypeId = cat.relationship_type_id || cat.relationshipTypeId
        return catTypeId === relationshipTypeId
      })

      return filteredCategories
        .map((cat: any) => ({
          value: cat.id,
          label: cat.category || cat.name || ''
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    [relationshipCategories]
  )

  // Validation rules
  const entryValidationRules = useMemo(
    () => [
      createCustomValidationRule(
        'Contact',
        (value, fieldName, record) => {
          const contactId = record.contactId
          if (!contactId || contactId === '') {
            return 'Contact is required'
          }
          return null
        },
        'contactId'
      ),
      createCustomValidationRule(
        'Type',
        (value, fieldName, record) => {
          const relationshipTypeId = record.relationshipTypeId
          if (!relationshipTypeId || relationshipTypeId === '') {
            return 'Type is required'
          }
          return null
        },
        'relationshipTypeId'
      ),
      createCustomValidationRule(
        'Category',
        (value, fieldName, record) => {
          const relationshipCategoryId = record.relationshipCategoryId
          if (!relationshipCategoryId || relationshipCategoryId === '') {
            return 'Category is required'
          }
          return null
        },
        'relationshipCategoryId'
      )
    ],
    []
  )

  // Handle add new entry
  const handleAdd = useCallback(() => {
    if (readOnly) return
    const newKey = `new-${crypto.randomUUID()}`
    const newEntry: OtherRow = {
      key: newKey,
      contactId: '',
      contactName: '',
      email: '',
      type: '',
      category: '',
      relationshipTypeId: '',
      relationshipCategoryId: '',
      relationshipId: null,
      originalEntry: null
    }
    setOriginalData(null)
    setIsAddingNew(true)
    const updatedEntries = [newEntry, ...editableEntries]
    setEditableEntries(updatedEntries)
    setEditingRowKey(newKey)
    setFirstErrorFieldKey({ rowKey: newKey, fieldKey: 'contact' })
  }, [readOnly, editableEntries])

  // Handle start editing
  const startEditing = useCallback(
    (rowKey: string) => {
      if (readOnly) return
      const currentEntry = editableEntries.find((entry) => entry.key === rowKey)
      if (currentEntry) {
        setOriginalData({ ...currentEntry })
      } else {
        setOriginalData(null)
      }
      setEditingRowKey(rowKey)
    },
    [readOnly, editableEntries]
  )

  // Handle cancel editing
  const cancelEditing = useCallback(
    (rowKey: string) => {
      if (rowKey) {
        const isNewEntry = rowKey && typeof rowKey === 'string' && rowKey.startsWith('new-')

        if (originalData) {
          setEditableEntries((prev) => prev.map((entry) => (entry.key === rowKey ? originalData : entry)))
          setIsAddingNew(false)
        } else if (isNewEntry) {
          setEditableEntries((prev) => prev.filter((entry) => entry.key !== rowKey))
          setIsAddingNew(false)
        }
      }
      setEditingRowKey(null)
      setSavingRowKey(null)
      setOriginalData(null)
    },
    [originalData]
  )

  // Wrapper for FormTable's onCancel
  const handleCancel = useCallback(() => {
    if (editingRowKey) {
      cancelEditing(editingRowKey)
    }
  }, [editingRowKey, cancelEditing])

  // Handle field change
  const handleFieldChange = useCallback(
    (rowKey: string, field: string, value: any) => {
      setEditableEntries((prev) => {
        return prev.map((entry) => {
          if (entry.key !== rowKey) return entry

          if (field === 'contact') {
            const contactId = typeof value === 'string' ? value : value?.value || value?.id || ''
            let contactName = typeof value === 'object' && value ? value.label || value.name || '' : ''
            let email = typeof value === 'object' && value ? value.email || '' : ''

            // If we have contact data, extract name and email
            if (value && typeof value === 'object') {
              if (value.data) {
                const contactData = value.data
                contactName =
                  contactData[UserEntryFields.prog__display_name] ||
                  `${contactData[UserEntryFields.contacts__first_name] || ''} ${contactData[UserEntryFields.contacts__last_name] || ''}`.trim() ||
                  contactData[UserEntryFields.contacts__email] ||
                  contactName ||
                  ''
                email = contactData[UserEntryFields.contacts__email] || email || ''
              } else if (value.label) {
                contactName = value.label
              }
            }

            return {
              ...entry,
              contactId,
              contactName,
              email
            }
          } else if (field === 'email') {
            return {
              ...entry,
              email: typeof value === 'string' ? value : value?.value || ''
            }
          } else if (field === 'type') {
            // Type field stores relationship type ID
            const relationshipTypeId = typeof value === 'object' ? value.value : value
            const typeLabel = getTypeLabelById(relationshipTypeId) || ''

            // When type changes, clear category to force reselection
            return {
              ...entry,
              type: typeLabel,
              category: '',
              relationshipTypeId,
              relationshipCategoryId: ''
            }
          } else if (field === 'category') {
            // Category field stores relationship category ID
            const relationshipCategoryId = typeof value === 'object' ? value.value : value
            const categoryLabel = getCategoryLabelById(relationshipCategoryId) || ''

            return {
              ...entry,
              category: categoryLabel,
              relationshipCategoryId
            }
          }
          return entry
        })
      })
    },
    [getCategoryLabelById, getTypeLabelById]
  )

  // Handle save
  const handleSave = useCallback(
    (rowKey: string) => {
      const entry = editableEntries.find((e) => e.key === rowKey)
      if (!entry) {
        cancelEditing(rowKey)
        return false
      }

      // Clear previous validation errors
      const errorKey = `${rowKey}-errors`
      setValidationErrors((prev) => ({
        ...prev,
        [errorKey]: {}
      }))
      setFirstErrorFieldKey(null)

      // Validate that the relationship category ID exists in Redux state
      if (entry.relationshipCategoryId && relationshipCategories.length > 0) {
        const categoryExists = relationshipCategories.some((cat: any) => cat.id === entry.relationshipCategoryId)
        if (!categoryExists) {
          const mappedErrors: Record<string, string> = {
            type: 'Selected relationship category is not valid. Please refresh the page and try again.'
          }
          setValidationErrors((prev) => ({
            ...prev,
            [errorKey]: mappedErrors
          }))
          setFirstErrorFieldKey({ rowKey: rowKey, fieldKey: 'type' })
          showErrorToast('Invalid relationship category. Please refresh the page to reload category options.', TOAST_CONFIG)
          return false
        }
      }

      // Validate the record
      const validationResult = validateAndShowErrors(entry, entryValidationRules)

      // If there are validation errors, highlight fields and focus first error
      if (!validationResult.isValid) {
        const mappedErrors: Record<string, string> = {}
        if (validationResult.errors.contactId) {
          mappedErrors.contact = validationResult.errors.contactId
        }
        if (validationResult.errors.relationshipTypeId) {
          mappedErrors.type = validationResult.errors.relationshipTypeId
        }
        if (validationResult.errors.relationshipCategoryId) {
          mappedErrors.category = validationResult.errors.relationshipCategoryId
        }

        setValidationErrors((prev) => ({
          ...prev,
          [errorKey]: mappedErrors
        }))

        const fieldPriority = ['contact', 'type', 'category']
        const firstErrorField = fieldPriority.find((field) => mappedErrors[field])

        if (firstErrorField) {
          setFirstErrorFieldKey({ rowKey: rowKey, fieldKey: firstErrorField })
        }

        return false
      }

      // All validations passed - save
      setSavingRowKey(rowKey)
      setEditingRowKey(null)
      setIsAddingNew(false)
      setFirstErrorFieldKey(null)
      setOriginalData(null)

      // Call onSave callback if provided
      if (onSave) {
        onSave(entry, editableEntries)
      }

      setSavingRowKey(null)
      return true
    },
    [editableEntries, cancelEditing, entryValidationRules, onSave, relationshipCategories]
  )

  // Handle remove (for FormTable delete button)
  const handleRemove = useCallback(
    async (rowKey: string) => {
      if (readOnly) return

      const entryToRemove = editableEntries.find((entry) => entry.key === rowKey)
      if (!entryToRemove) return

      // Set deleting state for loading indicator
      setDeletingRowKey(rowKey)

      // Clear editing state if this row was being edited
      if (editingRowKey === rowKey) {
        setEditingRowKey(null)
        setIsAddingNew(false)
      }

      // If it's a new entry, remove from local state immediately
      if (rowKey.startsWith('new-')) {
        setEditableEntries((prev) => prev.filter((entry) => entry.key !== rowKey))
        setDeletingRowKey(null)
        return
      }

      // For existing entries, optimistically remove from local state
      // The parent will refresh data from backend, which will sync via props
      setEditableEntries((prev) => prev.filter((entry) => entry.key !== rowKey))

      // Call onDelete to delete from backend
      if (onDelete) {
        try {
          await onDelete(entryToRemove)
        } catch (error) {
          // If delete fails, restore the entry
          setEditableEntries((prev) => {
            const existing = prev.find((e) => e.key === rowKey)
            if (!existing) {
              return [...prev, entryToRemove]
            }
            return prev
          })
          setDeletingRowKey(null)
          throw error
        }
      }

      setDeletingRowKey(null)
    },
    [readOnly, editableEntries, editingRowKey, onDelete]
  )

  // Auto-focus first error field when validation fails
  useEffect(() => {
    if (firstErrorFieldKey && editingRowKey === firstErrorFieldKey.rowKey) {
      const timeoutId = setTimeout(() => {
        const fieldKey = firstErrorFieldKey.fieldKey
        const tableRow = document.querySelector(`tr[data-row-key="${firstErrorFieldKey.rowKey}"]`)
        if (tableRow) {
          const cells = Array.from(tableRow.querySelectorAll('td'))
          const fieldToColumnMap: Record<string, number> = {
            contact: 0,
            email: 1,
            type: 2,
            category: 3
          }

          const columnIndex = fieldToColumnMap[fieldKey]
          if (columnIndex !== undefined) {
            const dataIndexCells = cells.filter((cell) => {
              return (
                cell.querySelector('input, textarea, .ant-select-selector, .ant-select') &&
                !cell.querySelector('button[class*="ant-btn"]')
              )
            })

            if (dataIndexCells[columnIndex]) {
              const targetCell = dataIndexCells[columnIndex]
              const input = targetCell.querySelector<HTMLElement>('input, textarea, .ant-select-selector, .ant-select')
              if (input) {
                if (input.classList.contains('ant-select')) {
                  const selector = input.querySelector<HTMLElement>('.ant-select-selector')
                  if (selector) {
                    selector.focus()
                    setTimeout(() => selector.click(), 50)
                  }
                } else {
                  input.focus()
                }
              }
            }
          }
        }
      }, 200)

      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [firstErrorFieldKey, editingRowKey])

  // Table columns with inline editing support
  const columns = useMemo(() => {
    return [
      {
        ...getColumnConfig('contact', { label: 'Name', required: true }, 200, 200),
        render: (_: any, record: OtherRow) => {
          const isEditing = editingRowKey === record.key
          const isNewEntry = record.key?.startsWith('new-')
          const contactId = record.contactId
          const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
          const errorKey = `${record.key}-errors`

          const contactValue =
            contactId && record.contactName
              ? { value: contactId, label: record.contactName, name: record.contactName, email: record.email }
              : contactId || ''

          // Check if this is a new contact (contactId === 'new' or contactName contains "(new)")
          const isNewContact =
            contactId === 'new' ||
            (record.contactName &&
              typeof record.contactName === 'string' &&
              record.contactName.toLowerCase().includes('(new)'))

          // Name is editable only for new entries when editing
          // For new entries: editable until saved (isNewEntry remains true until saved)
          // For existing entries: never editable
          const isContactEditable = isEditing && isNewEntry

          const contactFieldConfig: ContactFieldConfig = {
            key: 'contact',
            label: 'Name',
            type: FieldTypeEnum.CONTACT,
            value: contactValue,
            editable: isContactEditable,
            required: true,
            placeholder: 'Search contacts...',
            autoFocus: firstErrorField === 'contact',
            mode: 'single',
            showValueTag: false,
            hyperlinked: !isNewContact, // Disable hyperlinks for new contacts
            hyperlinkModule: 'contacts',
            getRecordId: (value) => {
              if (!value) return null
              return value?.value || value?.id || value?.data?.id || null
            }
          }

          // For new entries being edited, show editable field
          if (isContactEditable) {
            return (
              <InputManager
                fields={[contactFieldConfig]}
                inputMode='edit'
                labelPosition='none'
                onChange={(key, value) => handleFieldChange(record.key, key, value)}
                validationErrors={validationErrors[errorKey]}
                textClassName='text-sm'
              />
            )
          }

          // For existing entries or non-editing state, show read-only field
          return (
            <div
              onClick={readOnly ? undefined : () => startEditing(record.key)}
              className={readOnly ? '' : 'cursor-pointer'}
            >
              <InputManager
                fields={[contactFieldConfig]}
                inputMode='read'
                labelPosition='none'
                textClassName='text-sm'
              />
            </div>
          )
        }
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        width: 200,
        render: (_: any, record: OtherRow) => {
          const isEditing = editingRowKey === record.key
          const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
          const errorKey = `${record.key}-errors`

          const emailFieldConfig: TextFieldConfig = {
            key: 'email',
            label: 'Email',
            type: FieldTypeEnum.EMAIL,
            value: record.email || '',
            editable: isEditing,
            placeholder: 'Enter email...',
            autoFocus: firstErrorField === 'email'
          }

          if (!isEditing) {
            return (
              <div
                onClick={readOnly ? undefined : () => startEditing(record.key)}
                className={readOnly ? '' : 'cursor-pointer'}
              >
                <span className='text-sm'>{record.email || '-'}</span>
              </div>
            )
          }

          return (
            <InputManager
              fields={[emailFieldConfig]}
              inputMode='edit'
              labelPosition='none'
              onChange={(key, value) => handleFieldChange(record.key, key, value)}
              validationErrors={validationErrors[errorKey]}
              textClassName='text-sm'
            />
          )
        }
      },
      {
        ...getColumnConfig('type', { label: 'Type', required: true }, 150, 150),
        render: (_: any, record: OtherRow) => {
          const isEditing = editingRowKey === record.key
          const relationshipTypeId = record.relationshipTypeId
          const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
          const errorKey = `${record.key}-errors`

          const typeFieldConfig: SelectFieldConfig = {
            key: 'type',
            label: 'Type',
            type: FieldTypeEnum.SELECT,
            value: relationshipTypeId || '',
            editable: isEditing,
            required: true,
            options: typeOptions,
            placeholder: 'Select type...',
            allowClear: false,
            autoFocus: firstErrorField === 'type'
          }

          if (!isEditing) {
            return (
              <div
                onClick={readOnly ? undefined : () => startEditing(record.key)}
                className={readOnly ? '' : 'cursor-pointer'}
              >
                <span className='text-sm'>{record.type || '-'}</span>
              </div>
            )
          }

          return (
            <InputManager
              fields={[typeFieldConfig]}
              inputMode='edit'
              labelPosition='none'
              onChange={(key, value) => handleFieldChange(record.key, key, value)}
              validationErrors={validationErrors[errorKey]}
              textClassName='text-sm'
            />
          )
        }
      },
      {
        ...getColumnConfig('category', { label: 'Category', required: true }, 150, 150),
        render: (_: any, record: OtherRow) => {
          const isEditing = editingRowKey === record.key
          const relationshipCategoryId = record.relationshipCategoryId
          const relationshipTypeId = record.relationshipTypeId
          const firstErrorField = firstErrorFieldKey?.rowKey === record.key ? firstErrorFieldKey.fieldKey : null
          const errorKey = `${record.key}-errors`

          // Get category options based on selected type
          const categoryOptions = getCategoryOptionsByType(relationshipTypeId)

          const categoryFieldConfig: SelectFieldConfig = {
            key: 'category',
            label: 'Category',
            type: FieldTypeEnum.SELECT,
            value: relationshipCategoryId || '',
            editable: isEditing && !!relationshipTypeId, // Only editable if type is selected
            required: true,
            options: categoryOptions,
            placeholder: relationshipTypeId ? 'Select category...' : 'Select type first...',
            allowClear: false,
            autoFocus: firstErrorField === 'category',
            disabled: !relationshipTypeId // Disable if no type selected
          }

          if (!isEditing) {
            return (
              <div
                onClick={readOnly ? undefined : () => startEditing(record.key)}
                className={readOnly ? '' : 'cursor-pointer'}
              >
                <span className='text-sm'>{record.category || '-'}</span>
              </div>
            )
          }

          return (
            <InputManager
              fields={[categoryFieldConfig]}
              inputMode='edit'
              labelPosition='none'
              onChange={(key, value) => handleFieldChange(record.key, key, value)}
              validationErrors={validationErrors[errorKey]}
              textClassName='text-sm'
            />
          )
        }
      }
    ]
  }, [
    editingRowKey,
    readOnly,
    firstErrorFieldKey,
    validationErrors,
    typeOptions,
    getCategoryOptionsByType,
    handleFieldChange,
    startEditing
  ])

  return (
    <div className={className}>
      <FormTable
        title='Other'
        dataSource={editableEntries}
        columns={columns}
        includeActions={!readOnly}
        mode='existing'
        useDeleteConfirm={true}
        deleteConfirmTitle='Delete Contact'
        deleteConfirmDescription='Are you sure you want to remove this contact from the Other table?'
        isAddingNew={isAddingNew}
        onAdd={readOnly ? undefined : handleAdd}
        addButtonText=''
        addButtonIcon={faPlus}
        editingRowKey={editingRowKey}
        savingRowKey={savingRowKey}
        deletingRowKey={deletingRowKey}
        onSave={handleSave}
        onCancel={handleCancel}
        onRemove={readOnly ? undefined : handleRemove}
        emptyText='No other contacts found'
      />
    </div>
  )
}
